import { useCallback, useEffect, useRef, useState } from 'react';
export const UPDATED_BANNER_MS = 4000;
/**
 * WRT-27 marks a stale-key save as a branch version: source 'editor', kind
 * 'working', label "Unsaved edits from {time} (not current)".
 * TODO(WRT-27): until WRT-27 sets that label no version matches, and the
 * branch banner never shows.
 */ export function isBranchVersion(v) {
    return v.source === 'editor' && v.kind === 'working' && v.label.startsWith('Unsaved edits from');
}
/**
 * The "updated, reload" logic (WRT-04 §8a). The workspace query polls every
 * 10 s while visible; this compares its `current_version_id` with the version
 * the open session was opened on.
 *
 * - Same → nothing.
 * - My own editor save (source 'editor', created by me) → adopt it silently:
 *   the open editor already shows those bytes, so a reload would only flicker.
 * - Not dirty → `onRemount` + a 4-second "Updated to v{n}" notice.
 * - Dirty → a persistent "newer version" notice whose Reload remounts.
 */ export function useVersionPolling({ currentVersionId, dirty, dirtySince, onAdopt, onRemount, openedVersionId, recentVersions, selfId }) {
    const [notice, setNotice] = useState(null);
    const timer = useRef(null);
    const reloadedFrom = useRef(null);
    const clearTimer = useCallback(()=>{
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
    }, []);
    useEffect(()=>clearTimer, [
        clearTimer
    ]);
    useEffect(()=>{
        if (!currentVersionId || !openedVersionId || currentVersionId === openedVersionId) return;
        const version = recentVersions.find((v)=>v.id === currentVersionId);
        if (version && version.source === 'editor' && selfId && version.createdByUserId === selfId) {
            onAdopt(currentVersionId);
            return;
        }
        if (!dirty) {
            void onRemount(currentVersionId);
            clearTimer();
            setNotice({
                kind: 'updated',
                version
            });
            timer.current = setTimeout(()=>setNotice(null), UPDATED_BANNER_MS);
            return;
        }
        // A pending 4 s timer from an earlier 'updated' must not clear this one.
        clearTimer();
        setNotice({
            kind: 'newer',
            version,
            dirtySince
        });
    }, [
        currentVersionId,
        openedVersionId,
        dirty,
        dirtySince,
        recentVersions,
        selfId,
        onAdopt,
        onRemount,
        clearTimer
    ]);
    // After a Reload with unsaved edits, point at the branch version WRT-27 keeps them in.
    useEffect(()=>{
        const from = reloadedFrom.current;
        if (!from) return;
        const branch = recentVersions.find((v)=>isBranchVersion(v) && (!from.at || (v.createdAt ? v.createdAt.toDate().getTime() >= from.at.getTime() : true)));
        if (branch) {
            reloadedFrom.current = null;
            setNotice({
                kind: 'branch',
                version: branch,
                dirtySince: from.at
            });
        }
    }, [
        recentVersions
    ]);
    const reload = useCallback(()=>{
        if (!currentVersionId) return;
        reloadedFrom.current = {
            at: dirtySince
        };
        clearTimer();
        setNotice(null);
        void onRemount(currentVersionId);
    }, [
        currentVersionId,
        dirtySince,
        onRemount,
        clearTimer
    ]);
    const dismiss = useCallback(()=>{
        clearTimer();
        setNotice(null);
    }, [
        clearTimer
    ]);
    return {
        notice,
        reload,
        dismiss
    };
}
